/**
 * デモモード制限機能の中央制御サービス
 * FL-154: デモ体験時の外部サービス無効化
 */

import { isDemoMode } from '../utils/environment';
import logger from '../utils/logger';
import {
  DemoFeature,
  RestrictionResult,
  ServiceType,
  MockResponse,
  EmailMockResponse,
  AIApiMockResponse,
  LineNotificationMockResponse,
  DemoModeConfig,
} from '../types/demoMode';

/**
 * デモモード制限の中央制御サービス
 */
export class DemoModeService {
  private static instance: DemoModeService;
  private config: DemoModeConfig;

  private constructor() {
    this.config = this.getDefaultConfig();
  }

  /**
   * シングルトンインスタンスを取得
   */
  public static getInstance(): DemoModeService {
    if (!DemoModeService.instance) {
      DemoModeService.instance = new DemoModeService();
    }
    return DemoModeService.instance;
  }

  /**
   * デフォルト設定を取得
   */
  private getDefaultConfig(): DemoModeConfig {
    return {
      enabled: isDemoMode(),
      restrictedFeatures: [
        DemoFeature.EMAIL_SEND,
        DemoFeature.USER_REGISTRATION,
        DemoFeature.PASSWORD_RESET,
        DemoFeature.AI_API,
        DemoFeature.LINE_NOTIFICATION,
        DemoFeature.GOOGLE_MAPS,
        DemoFeature.SMS_SEND,
        DemoFeature.EXTERNAL_DATA_SYNC,
      ],
      customMessages: {
        [DemoFeature.EMAIL_SEND]: 'デモ環境ではメール送信は実行されません',
        [DemoFeature.USER_REGISTRATION]: 'デモ環境では新規ユーザー登録は無効です',
        [DemoFeature.PASSWORD_RESET]: 'デモ環境ではパスワードリセットは無効です',
        [DemoFeature.AI_API]: 'デモ環境ではAI API呼び出しは実行されません',
        [DemoFeature.LINE_NOTIFICATION]: 'デモ環境ではLINE通知は送信されません',
        [DemoFeature.GOOGLE_MAPS]: 'デモ環境ではGoogle Maps APIは呼び出されません',
        [DemoFeature.SMS_SEND]: 'デモ環境ではSMS送信は実行されません',
        [DemoFeature.EXTERNAL_DATA_SYNC]: 'デモ環境では外部データ同期は実行されません',
      },
    };
  }

  /**
   * デモモードが有効かどうかを確認
   */
  public isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * 指定された機能が制限されているかチェック
   */
  public checkRestriction(feature: DemoFeature): RestrictionResult {
    const isRestricted = this.isEnabled() && this.config.restrictedFeatures.includes(feature);

    if (!isRestricted) {
      return {
        isRestricted: false,
        message: '制限なし',
      };
    }

    const customMessage = this.config.customMessages?.[feature];
    const message = customMessage || `デモ環境では${feature}は制限されています`;

    return {
      isRestricted: true,
      message,
      recommendedAction: 'デモ以外の環境で実際の機能をお試しください',
    };
  }

  /**
   * モックレスポンスを生成
   */
  public getMockResponse(serviceType: ServiceType, options?: any): MockResponse {
    const baseResponse: MockResponse = {
      success: true,
      message: `デモモード: ${serviceType}の処理をスキップしました`,
      isDemoMock: true,
    };

    switch (serviceType) {
      case ServiceType.EMAIL:
        return this.createEmailMockResponse(options, baseResponse);

      case ServiceType.AI_API:
        return this.createAIMockResponse(options, baseResponse);

      case ServiceType.LINE:
        return this.createLineMockResponse(options, baseResponse);

      case ServiceType.GOOGLE_MAPS:
        return {
          ...baseResponse,
          data: {
            location: { lat: 35.6762, lng: 139.6503 }, // 東京駅
            address: '東京都千代田区丸の内1丁目',
          },
        };

      case ServiceType.SMS:
        return {
          ...baseResponse,
          data: {
            messageId: `demo_sms_${Date.now()}`,
            status: 'sent',
          },
        };

      default:
        return baseResponse;
    }
  }

  /**
   * メール送信のモックレスポンス作成
   */
  private createEmailMockResponse(options: any, baseResponse: MockResponse): EmailMockResponse {
    return {
      ...baseResponse,
      messageId: `demo_email_${Date.now()}`,
      to: options?.to || ['demo@example.com'],
      subject: options?.subject || 'デモメール',
    };
  }

  /**
   * AI APIのモックレスポンス作成
   */
  private createAIMockResponse(_options: any, baseResponse: MockResponse): AIApiMockResponse {
    return {
      ...baseResponse,
      response: 'これはデモ環境でのAI応答例です。実際の環境では実際のAIが応答します。',
      model: 'demo-model',
      usage: {
        promptTokens: 10,
        completionTokens: 20,
        totalTokens: 30,
      },
    };
  }

  /**
   * LINE通知のモックレスポンス作成
   */
  private createLineMockResponse(
    options: any,
    baseResponse: MockResponse
  ): LineNotificationMockResponse {
    return {
      ...baseResponse,
      userId: options?.userId || 'demo-user',
      messageType: options?.messageType || 'text',
      content: options?.content || 'デモ通知メッセージ',
    };
  }

  /**
   * 制限メッセージを取得
   */
  public getRestrictionMessage(feature: DemoFeature): string {
    const restriction = this.checkRestriction(feature);
    return restriction.message;
  }

  /**
   * 制限対象機能の一覧を取得
   */
  public getRestrictedFeatures(): DemoFeature[] {
    if (!this.isEnabled()) {
      return [];
    }
    return [...this.config.restrictedFeatures];
  }

  /**
   * ログ出力（デモモード動作の明示化）
   */
  public logDemoAction(feature: DemoFeature, action: string, details?: any): void {
    if (!this.isEnabled()) {
      return;
    }

    logger.info(`[DEMO] ${feature}: ${action}`, {
      feature,
      action,
      demoMode: true,
      details,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * デモモードの設定を更新（テスト用）
   */
  public updateConfig(config: Partial<DemoModeConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * デモモードの統計情報を取得
   */
  public getStats(): {
    enabled: boolean;
    restrictedFeaturesCount: number;
    features: string[];
  } {
    return {
      enabled: this.isEnabled(),
      restrictedFeaturesCount: this.config.restrictedFeatures.length,
      features: this.config.restrictedFeatures.map(f => f.toString()),
    };
  }
}

// シングルトンインスタンスをエクスポート
export const demoModeService = DemoModeService.getInstance();

// 便利関数をエクスポート
export const isFeatureRestricted = (feature: DemoFeature): boolean => {
  return demoModeService.checkRestriction(feature).isRestricted;
};

export const getRestrictionMessage = (feature: DemoFeature): string => {
  return demoModeService.getRestrictionMessage(feature);
};

export const logDemoAction = (feature: DemoFeature, action: string, details?: any): void => {
  demoModeService.logDemoAction(feature, action, details);
};
